import { ChangeEvent, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import ProductsGrid from "./ProductsGrid";

const FILTERS = [
  { label: "Tout", slug: "", description: "Toute la collection" },
  { label: "Nouveautes", slug: "news", description: "Les derniers modeles arrives" },
  { label: "Chaussures", slug: "shoes", description: "Des paires qui font la difference" },
  { label: "Sacs", slug: "bags", description: "Les sacs qui completent le look" },
];

export default function Products() {
  const navigate = useNavigate();
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchFromUrl = searchParams.get("search") ?? "";
  const [search, setSearch] = useState(searchFromUrl);

  const activeFilter = category ?? "";

  useEffect(() => {
    setSearch(searchFromUrl);
  }, [searchFromUrl]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearch(value);
    if (value.trim()) {
      setSearchParams({ search: value }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  return (
    <section className="px-4 pb-20 pt-36 md:px-8 lg:px-10 lg:pt-40">
      <div className="mx-auto max-w-7xl">
        <div className="surface-panel rounded-[36px] p-6 md:p-8 lg:p-10">
          <div className="grid gap-8 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
            <div>
              <p className="brand-eyebrow">Boutique</p>
              <h1 className="brand-display mt-4 text-5xl font-bold leading-[0.95] text-[#21140f] md:text-7xl">
                Trouvez la piece faite pour vous.
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-8 text-[#735c51]">
                Parcourez nos collections, filtrez selon vos envies et trouvez
                en un instant le modele qui vous correspond.
              </p>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              <div className="rounded-[26px] bg-[#241712] px-6 py-5 text-[#fff4ea]">
                <p className="text-xs uppercase tracking-[0.28em] text-[#dfbfa9]">
                  Facile a parcourir
                </p>
                <p className="mt-3 text-sm leading-7">
                  Recherchez rapidement et accedez facilement a vos coups de
                  coeur.
                </p>
              </div>
              <div className="rounded-[26px] bg-[#f0dccc] px-6 py-5 text-[#3d261a]">
                <p className="text-xs uppercase tracking-[0.28em] text-[#89604c]">
                  Pieces bien mises en valeur
                </p>
                <p className="mt-3 text-sm leading-7">
                  Des photos, des prix clairs et des details utiles pour faire
                  votre choix.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-10 grid gap-5 lg:grid-cols-[1.1fr_1.4fr]">
            <div className="rounded-[30px] bg-white/70 p-3">
              <div className="flex items-center gap-3 rounded-[22px] bg-[#fff9f3] px-4 py-4">
                <span className="text-lg text-[#7a4328]">⌕</span>
                <input
                  type="text"
                  placeholder="Rechercher une piece, une couleur ou un style"
                  className="w-full border-0 bg-transparent text-sm text-[#221510] outline-none ring-0 focus:border-0 focus:outline-none focus:ring-0 placeholder:text-[#a28b7d]"
                  value={search}
                  onChange={handleSearchChange}
                />
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {FILTERS.map((filter) => {
                const isActive = activeFilter === filter.slug;

                return (
                  <button
                    key={filter.label}
                    type="button"
                    onClick={() =>
                      navigate(filter.slug ? `/products/${filter.slug}` : "/products")
                    }
                    className={`rounded-[26px] border px-5 py-4 text-left transition ${
                      isActive
                        ? "border-[rgba(110,45,22,0.22)] bg-[linear-gradient(135deg,#2c1b15,#8f4225)] text-[#fff4ea]"
                        : "border-[rgba(62,35,24,0.08)] bg-white/68 text-[#3d281d] hover:border-[rgba(110,45,22,0.18)] hover:bg-white/90"
                    }`}
                  >
                    <div className="text-[11px] font-semibold uppercase tracking-[0.28em]">
                      {filter.label}
                    </div>
                    <p
                      className={`mt-2 text-sm leading-6 ${
                        isActive ? "text-[#f1d7c4]" : "text-[#7b6255]"
                      }`}
                    >
                      {filter.description}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-10">
          <ProductsGrid category={activeFilter} search={search} />
        </div>
      </div>
    </section>
  );
}
